import { useMemo } from 'react';
import type { EndingReport, PlayerState } from '@core/types';
import { money } from './Hud';
import './Ending.css';

interface Props {
  state: PlayerState;
  report: EndingReport;
  onNewLife: () => void;
  onSameSeed: () => void;
  onTitle: () => void;
}

/**
 * The ending is the strongest screen in the game, because it is the only place
 * the whole run becomes legible. It reports eight independent dimensions rather
 * than one score, then offers an interpretation — never a verdict.
 */
export function EndingScreen({ state, report, onNewLife, onSameSeed, onTitle }: Props) {
  const best = useMemo(
    () => [...report.dimensions].sort((a, b) => b.score - a.score)[0],
    [report.dimensions],
  );
  const worst = useMemo(
    () => [...report.dimensions].sort((a, b) => a.score - b.score)[0],
    [report.dimensions],
  );

  return (
    <main className="ending">
      <div className="ending__inner">
        <header className="ending__head">
          <p className="eyebrow">
            {state.identity.name} · {state.startAge}–{report.age} · {report.cause}
          </p>
          <h1 className="ending__archetype">{report.archetypeLabel}</h1>
          <p className="ending__headline">{report.headline}</p>
          <p className="ending__narrative">{report.archetypeNarrative}</p>
          <p className="ending__epitaph">“{report.epitaph}”</p>
        </header>

        <section className="ending__dims" aria-label="How the life measured up">
          {report.dimensions.map((dimension) => (
            <div key={dimension.key} className="dim">
              <div className="dim__top">
                <span className="dim__label">{dimension.label}</span>
                <span className="dim__score numeral">{Math.round(dimension.score)}</span>
              </div>
              <span className="dim__track" aria-hidden="true">
                <span className="dim__fill" style={{ width: `${dimension.score}%` }} />
              </span>
              <span className="dim__note">{dimension.note}</span>
            </div>
          ))}
        </section>

        <section className="ending__split">
          <div className="ending__facts">
            <h2 className="ending__sub">The numbers</h2>
            <Fact label="Net worth at the end" value={money(report.netWorth)} />
            <Fact label="Peak net worth" value={money(state.finance.peakNetWorth)} />
            <Fact label="Earned across a lifetime" value={money(state.finance.lifetimeEarnings)} />
            <Fact label="Children" value={String(state.family.childrenBorn)} />
            <Fact
              label="Marriages"
              value={`${state.family.marriages}${state.family.divorces > 0 ? ` (${state.family.divorces} ended)` : ''}`}
            />
            <Fact label="Career changes" value={String(state.career.pivots)} />
            <Fact label="Promotions" value={String(state.career.promotions)} />
            <Fact label="Redundancies" value={String(state.career.layoffs)} />
            <Fact label="Burnouts" value={String(state.burnout.episodes)} />
            <Fact label="Crises weathered" value={String(state.counters['crises'] ?? 0)} />
            <Fact label="Decisions made" value={String(state.decisions.length)} />
            <Fact
              label="Things never got round to"
              value={String(state.inertia.decayedCount)}
            />
          </div>

          <div className="ending__lists">
            {report.highlights.length > 0 && (
              <div className="ending__list">
                <h2 className="ending__sub">Best of it</h2>
                <ul>
                  {report.highlights.map((item, index) => (
                    <li key={`${item}-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
            {report.setbacks.length > 0 && (
              <div className="ending__list ending__list--bad">
                <h2 className="ending__sub">Hardest of it</h2>
                <ul>
                  {report.setbacks.map((item, index) => (
                    <li key={`${item}-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
            {report.unfinished.length > 0 && (
              <div className="ending__list ending__list--open">
                <h2 className="ending__sub">Left unfinished</h2>
                <ul>
                  {report.unfinished.map((item, index) => (
                    <li key={`${item}-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>

        {best && worst && best.key !== worst.key && (
          <p className="ending__verdict">
            Strongest in <strong>{best.label.toLowerCase()}</strong>, thinnest in{' '}
            <strong>{worst.label.toLowerCase()}</strong>. Most lives are like that.
          </p>
        )}

        <section className="ending__timeline">
          <h2 className="ending__sub">The whole arc</h2>
          <ol className="arc">
            {report.timeline.map((entry, index) => (
              <li key={`${entry.age}-${index}`} className={`arc__item arc__item--${entry.tone}`}>
                <span className="arc__age numeral">{entry.age}</span>
                <span className="arc__title">{entry.title}</span>
                {entry.detail && <span className="arc__detail">{entry.detail}</span>}
              </li>
            ))}
          </ol>
        </section>

        <footer className="ending__actions">
          <button type="button" className="btn btn--primary" onClick={onNewLife}>
            Live another life
          </button>
          <button type="button" className="btn" onClick={onSameSeed}>
            Replay this seed
          </button>
          <button type="button" className="btn btn--ghost" onClick={onTitle}>
            Back to the start
          </button>
          <span className="ending__seed">
            Seed <strong>{state.rng.seed}</strong>
          </span>
        </footer>
      </div>
    </main>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="fact">
      <span className="fact__label">{label}</span>
      <span className="fact__value numeral">{value}</span>
    </div>
  );
}
